/**
 * Space weather: the answers of the provider proxy's space weather operations read into plain values for Teslyn
 * Field's Space weather screen and the Diurnal QC strip (docs/specs/space-weather.md §3, §4; parity C6). Pure: the
 * same text always gives the same values; nothing here fetches, stores or reads a clock of its own.
 *
 * Sources and terms are in `space-weather-sources.ts`; the verdict "What this means for flying today" is
 * `spaceWeatherOutlook()` in `space-weather-outlook.ts`; the storm notices' seam is `space-weather-alerts.ts`.
 *
 * Every BoM parser is built against the examples in BoM's published API documentation
 * (https://sws-data.sws.bom.gov.au/api-docs, read 26 Oct 2026; tests/fixtures/space-weather/bom-api-docs/), because
 * no genuine BoM answer exists until an organisation's key is entered (decision 687).
 */
import { BOM_K_LOCATIONS, type BomKLocation } from './provider-protocols.js';
import { parseEnlilList } from './space-weather-enlil.js';
import {
  lenientJson,
  readBomSwsAnswer,
  SOLAR_WIND_MEAN_MINUTES,
  SWCOM_STORIES_MAX,
  sourceLink,
  spaceWeatherSource,
  type BomGeostat,
  type ReadingItem,
  type SolarWind24h,
  type SpaceweatherComNow,
  type SpaceweatherComStory,
} from './space-weather-sources.js';

export type {
  BomGeostat,
  ReadingItem,
  SolarWind24h,
  SpaceweatherComNow,
  SpaceweatherComStory,
} from './space-weather-sources.js';

// ---------------------------------------------------------------------------------------------- times

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** BoM's "yyyy-mm-dd hh:mm:ss" (UTC) or "yyyy-mm-dd" to ISO; null when it is not a whole time (BoM's own docs carry "2015-02-07 08:45:"). */
export function bomTime(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/.exec(s.trim());
  if (!m) return null;
  const ms = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** NOAA's "2026-10-26T05:36:00Z", "2026-10-26T05:36:00" or "2026-10-26 05:17:56.783" (UTC) to ISO. */
export function noaaTime(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  const t = s.trim().replace(' ', 'T');
  const ms = Date.parse(/[zZ]|[+-]\d{2}:\d{2}$/.test(t) ? t : `${t}Z`);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** NOAA message times: "2026 Oct 23 1926 UTC" to ISO. */
export function noaaMessageTime(s: string): string | null {
  const m = /(\d{4})\s+([A-Z][a-z]{2})\s+(\d{1,2})\s+(\d{2})(\d{2})\s*UTC/.exec(s);
  if (!m) return null;
  const mo = MONTHS.indexOf(m[2]!);
  if (mo < 0) return null;
  return new Date(Date.UTC(+m[1]!, mo, +m[3]!, +m[4]!, +m[5]!)).toISOString();
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const rows = (body: string): Record<string, unknown>[] => {
  const v = lenientJson(body);
  return Array.isArray(v) ? v.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object') : [];
};

// ---------------------------------------------------------------------------------------------- BoM

/** A BoM answer that is an error, in words for its section ("BoM refused: Unsupported option value …"). */
export interface BomFailure {
  readonly error: string;
}

/**
 * The rows of a BoM answer. BoM's genuine A and Dst answers hold them one array deeper than its documentation prints
 * (`{"data":[[{"index":"2",...}]]}`, captured 26 Oct 2026), K answers and the documentation a flat list: both read.
 */
function bomData(body: string): readonly Record<string, unknown>[] | BomFailure {
  const a = readBomSwsAnswer(body);
  if (!a) return { error: 'BoM sent an answer Teslyn cannot read.' };
  if ('errors' in a)
    return {
      error: `BoM refused: ${a.errors.map((e) => `${e.message || 'error'} (code ${e.code})`).join('; ')}`,
    };
  return a.data
    .flatMap((r) => (Array.isArray(r) ? (r as unknown[]) : [r]))
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r));
}

export const isBomFailure = (v: unknown): v is BomFailure =>
  !!v && typeof v === 'object' && typeof (v as BomFailure).error === 'string';

export interface BomIndex {
  /** Start of the period (K: 3 hours; A: the day; Dst: the moment), ISO UTC. */
  readonly validTime: string;
  /** When BoM made the K value (re-analysed within its period); null for A and Dst. */
  readonly analysisTime: string | null;
  readonly index: number;
}

/**
 * `get-k-index`, `get-a-index`, `get-dst-index`: the newest value, or null when BoM has none (an empty `data`, as
 * for a site that stopped reporting); a failure in words. A and Dst values come as strings (BoM's genuine answers).
 */
export function parseBomIndex(body: string): BomIndex | null | BomFailure {
  const d = bomData(body);
  if (isBomFailure(d)) return d;
  let best: BomIndex | null = null;
  for (const r of d) {
    const validTime = bomTime(r.valid_time);
    const index = num(r.index);
    if (!validTime || index === null) continue;
    if (!best || validTime > best.validTime)
      best = { validTime, analysisTime: bomTime(r.analysis_time), index };
  }
  return best;
}

export interface BomMagAlert {
  readonly startTime: string | null;
  readonly validUntil: string | null;
  /** NOAA G scale 1 to 5. */
  readonly gScale: number | null;
  /** minor, major, severe. */
  readonly description: string | null;
}

/** `get-mag-alert`: the current magnetic alert, or null when none is current. */
export function parseBomMagAlert(body: string): BomMagAlert | null | BomFailure {
  const d = bomData(body);
  if (isBomFailure(d)) return d;
  const r = d[0];
  if (!r) return null;
  return {
    startTime: bomTime(r.start_time),
    validUntil: bomTime(r.valid_until),
    gScale: num(r.g_scale),
    description: str(r.description),
  };
}

export interface BomMagWarning {
  readonly issueTime: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly cause: string | null;
  readonly activity: readonly { readonly date: string; readonly forecast: string }[];
  readonly comments: string | null;
}

/** `get-mag-warning`: the current geophysical warning, or null when none is active. */
export function parseBomMagWarning(body: string): BomMagWarning | null | BomFailure {
  const d = bomData(body);
  if (isBomFailure(d)) return d;
  const r = d[0];
  if (!r) return null;
  const activity = (Array.isArray(r.activity) ? r.activity : [])
    .map((a) => (a && typeof a === 'object' ? (a as Record<string, unknown>) : {}))
    .map((a) => ({ date: str(a.date) ?? '', forecast: str(a.forecast) ?? '' }))
    .filter((a) => /^\d{4}-\d{2}-\d{2}$/.test(a.date) && a.forecast);
  return {
    issueTime: bomTime(r.issue_time),
    startDate: str(r.start_date)?.slice(0, 10) ?? null,
    endDate: str(r.end_date)?.slice(0, 10) ?? null,
    cause: str(r.cause),
    activity,
    comments: str(r.comments),
  };
}

export type BomAuroraKind = 'alert' | 'watch' | 'outlook';

export interface BomAurora {
  readonly kind: BomAuroraKind;
  /** Alert: start time; watch and outlook: issue time. */
  readonly time: string | null;
  /** Alert: valid until; watch and outlook: their last day. */
  readonly until: string | null;
  readonly kAus: number | null;
  readonly latBand: string | null;
  readonly text: string | null;
}

/** `get-aurora-alert`, `-watch`, `-outlook`: the current notice, or null. */
export function parseBomAurora(kind: BomAuroraKind, body: string): BomAurora | null | BomFailure {
  const d = bomData(body);
  if (isBomFailure(d)) return d;
  const r = d[0];
  if (!r) return null;
  return {
    kind,
    time: kind === 'alert' ? bomTime(r.start_time) : bomTime(r.issue_time),
    until: kind === 'alert' ? bomTime(r.valid_until) : (str(r.end_date)?.slice(0, 10) ?? null),
    kAus: num(r.k_aus),
    latBand: str(r.lat_band),
    text: str(r.description) ?? str(r.comments),
  };
}

// ---------------------------------------------------------------------------------------------- BoM K sites

export interface BomKSite {
  readonly location: Exclude<BomKLocation, 'Australian region'>;
  readonly lat: number;
  readonly lon: number;
}

/**
 * The 13 Australian K-index sites of the BoM API, with approximate positions (the towns, to about 0.1°; BoM does
 * not publish the magnetometers' positions in the API). Used only to rank them by distance from the blocks.
 */
export const BOM_K_SITES: readonly BomKSite[] = [
  { location: 'Alice Springs', lat: -23.7, lon: 133.88 },
  { location: 'Canberra', lat: -35.32, lon: 149.36 },
  { location: 'Cocos Island', lat: -12.19, lon: 96.83 },
  { location: 'Narrabri', lat: -30.32, lon: 149.78 },
  { location: 'Darwin', lat: -12.46, lon: 130.84 },
  { location: 'Hobart', lat: -42.88, lon: 147.33 },
  { location: 'Launceston', lat: -41.43, lon: 147.14 },
  { location: 'Learmonth', lat: -22.22, lon: 114.1 },
  { location: 'Melbourne', lat: -37.81, lon: 144.96 },
  { location: 'Norfolk Island', lat: -29.04, lon: 167.95 },
  { location: 'Perth', lat: -31.95, lon: 115.86 },
  { location: 'Sydney', lat: -33.87, lon: 151.21 },
  { location: 'Townsville', lat: -19.26, lon: 146.82 },
];

// a compile-time check that every site is one of the API's locations
const _sitesAreLocations: readonly BomKLocation[] = BOM_K_SITES.map((s) => s.location);
void _sitesAreLocations;
void BOM_K_LOCATIONS;

/** Great-circle distance, km. */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = (d: number): number => (d * Math.PI) / 180;
  const dLat = r(b.lat - a.lat);
  const dLon = r(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The BoM K sites nearest the blocks' centroid first, with their distances. */
export function nearestBomKSites(centre: {
  lat: number;
  lon: number;
}): readonly (BomKSite & { readonly km: number })[] {
  return BOM_K_SITES.map((s) => ({ ...s, km: distanceKm(centre, s) })).sort((a, b) => a.km - b.km);
}

/** A site's newest K older than this is passed over for the next nearest (Learmonth and Macquarie Island outages, §3.1). */
export const BOM_SITE_STALE_MS = 6 * 3_600_000;

/**
 * The nearest site whose newest K is at most 6 hours old (by its period start + 3 h, the end of the period it
 * describes), from the answers held for each site; null when none is.
 */
export function chooseNearestK(
  ranked: readonly (BomKSite & { readonly km: number })[],
  answers: Readonly<Partial<Record<string, BomIndex | null>>>,
  nowMs: number,
): {
  readonly site: BomKSite & { readonly km: number };
  readonly k: BomIndex;
  readonly passedOver: readonly string[];
} | null {
  const passedOver: string[] = [];
  for (const s of ranked) {
    const k = answers[s.location];
    if (k === undefined) continue;
    if (k && nowMs - (Date.parse(k.validTime) + 3 * 3_600_000) <= BOM_SITE_STALE_MS)
      return { site: s, k, passedOver };
    passedOver.push(s.location);
  }
  return null;
}

// ---------------------------------------------------------------------------------------------- NOAA

export interface NoaaScales {
  /** R, S, G now (`"0"`). */
  readonly now: { readonly r: number | null; readonly s: number | null; readonly g: number | null };
  readonly time: string | null;
  /** Today and the next days (`"1"` to `"3"`): probabilities of R1-R2, R3+ and S1+ in %, and the G level expected. */
  readonly days: readonly {
    readonly date: string;
    readonly g: number | null;
    readonly rMinorProb: number | null;
    readonly rMajorProb: number | null;
    readonly sProb: number | null;
  }[];
}

/** `/products/noaa-scales.json`. */
export function parseNoaaScales(body: string): NoaaScales | null {
  const v = lenientJson(body);
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, Record<string, unknown>>;
  const level = (x: unknown, k: string): number | null => {
    const r = x && typeof x === 'object' ? (x as Record<string, unknown>)[k] : null;
    const s = r && typeof r === 'object' ? (r as Record<string, unknown>).Scale : null;
    return num(s);
  };
  const p = (x: unknown, k: string, f: string): number | null => {
    const r = x && typeof x === 'object' ? (x as Record<string, unknown>)[k] : null;
    return r && typeof r === 'object' ? num((r as Record<string, unknown>)[f]) : null;
  };
  const z = o['0'];
  if (!z) return null;
  const days = ['1', '2', '3']
    .map((k) => o[k])
    .filter((d): d is Record<string, unknown> => !!d)
    .map((d) => ({
      date: str(d.DateStamp) ?? '',
      g: level(d, 'G'),
      rMinorProb: p(d, 'R', 'MinorProb'),
      rMajorProb: p(d, 'R', 'MajorProb'),
      sProb: p(d, 'S', 'Prob'),
    }));
  return {
    now: { r: level(z, 'R'), s: level(z, 'S'), g: level(z, 'G') },
    time: z.DateStamp && z.TimeStamp ? noaaTime(`${String(z.DateStamp)}T${String(z.TimeStamp)}`) : null,
    days,
  };
}

export interface KpPoint {
  /** Start of the 3-hour bin (or the minute), ISO UTC. */
  readonly t: string;
  readonly kp: number;
  readonly kind: 'observed' | 'estimated' | 'predicted';
}

/** `/products/noaa-planetary-k-index.json` (7 days, all observed). */
export function parseKpHistory(body: string): readonly KpPoint[] {
  return rows(body)
    .map((r) => ({ t: noaaTime(r.time_tag), kp: num(r.Kp ?? r.kp) }))
    .filter((r): r is { t: string; kp: number } => r.t !== null && r.kp !== null)
    .map((r) => ({ ...r, kind: 'observed' as const }));
}

/** `/products/noaa-planetary-k-index-forecast.json`: observed, estimated and predicted 3-hour Kp. */
export function parseKpForecast(body: string): readonly KpPoint[] {
  return rows(body)
    .map((r) => ({
      t: noaaTime(r.time_tag),
      kp: num(r.kp),
      kind: r.observed === 'predicted' ? 'predicted' : r.observed === 'estimated' ? 'estimated' : 'observed',
    }))
    .filter((r): r is KpPoint => r.t !== null && r.kp !== null);
}

/** `/json/planetary_k_index_1m.json`: the newest estimated Kp (minute value). */
export function parseKp1m(body: string): { readonly t: string; readonly kp: number } | null {
  let best: { t: string; kp: number } | null = null;
  for (const r of rows(body)) {
    const t = noaaTime(r.time_tag);
    const kp = num(r.estimated_kp ?? r.kp_index);
    if (t && kp !== null && (!best || t > best.t)) best = { t, kp };
  }
  return best;
}

export interface SolarWindNow {
  readonly speed: number | null;
  readonly speedTime: string | null;
  readonly bt: number | null;
  readonly bz: number | null;
  readonly magTime: string | null;
}

/** The proxy's `noaa_solar_wind_now` (`{ speed: [...], mag: [...] }` from SWPC's two summary files). */
export function parseSolarWindNow(body: string): SolarWindNow | null {
  const v = lenientJson(body);
  if (!v || typeof v !== 'object') return null;
  const o = v as { speed?: unknown; mag?: unknown };
  const first = (x: unknown): Record<string, unknown> =>
    Array.isArray(x) && x[0] && typeof x[0] === 'object' ? (x[0] as Record<string, unknown>) : {};
  const s = first(o.speed);
  const m = first(o.mag);
  return {
    speed: num(s.proton_speed),
    speedTime: noaaTime(s.time_tag),
    bt: num(m.bt),
    bz: num(m.bz_gsm),
    magTime: noaaTime(m.time_tag),
  };
}

/** The proxy's `noaa_solar_wind_24h` (10-minute means of the active spacecraft, `reduceRtsw`). */
export function parseSolarWind24h(body: string): SolarWind24h | null {
  const v = lenientJson(body);
  if (!v || typeof v !== 'object' || !Array.isArray((v as SolarWind24h).points)) return null;
  return v as SolarWind24h;
}

/** "SOLAR1" as people read it. */
export function spacecraftName(source: string | null): string {
  if (!source) return 'the active spacecraft';
  const known: Record<string, string> = { SOLAR1: 'SOLAR-1', DSCOVR: 'DSCOVR', ACE: 'ACE', IMAP: 'IMAP' };
  return known[source.toUpperCase()] ?? source;
}

/** L1 to Earth, km (the distance SWPC uses for its arrival estimate). */
export const L1_DISTANCE_KM = 1_500_000;

/** Minutes the solar wind now at L1 takes to reach Earth at `speed` km/s; null without a speed. */
export function l1LeadMinutes(speed: number | null): number | null {
  return speed && speed > 0 ? Math.round(L1_DISTANCE_KM / speed / 60) : null;
}

/** The lowest 30-minute mean Bz of the last `windowMin` minutes, with the mean speed then (spec §4.3, Unsettled). */
export function sustainedSouthwardBz(
  wind: SolarWind24h | null,
  nowMs: number,
  windowMin = 60,
  heldMin = 30,
): { readonly bz: number; readonly speed: number | null } | null {
  if (!wind) return null;
  const recent = wind.points.filter((p) => nowMs - Date.parse(p.t) <= windowMin * 60_000 && p.bz !== null);
  // `heldMin` of consecutive means (3 of NOAA's 10-minute means for 30 minutes)
  const n = Math.max(1, Math.round(heldMin / SOLAR_WIND_MEAN_MINUTES));
  let best: { bz: number; speed: number | null } | null = null;
  for (let i = 0; i + n <= recent.length; i++) {
    const w = recent.slice(i, i + n);
    const bz = w.reduce((s, p) => s + p.bz!, 0) / n;
    const sp = w.filter((p) => p.speed !== null);
    const speed = sp.length ? sp.reduce((s, p) => s + p.speed!, 0) / sp.length : null;
    if (!best || bz < best.bz) best = { bz, speed };
  }
  return best;
}

// ---------------------------------------------------------------------------------------------- NOAA messages

export type NoaaMessageKind = 'watch' | 'warning' | 'alert' | 'summary' | 'cancel' | 'other';

export interface NoaaMessage {
  readonly productId: string;
  readonly issued: string;
  readonly serial: string | null;
  readonly kind: NoaaMessageKind;
  /** About the geomagnetic field (K04 to K09 W and A, A20F to A99F watches, SGIW, MSIS); the rest are counted apart. */
  readonly magnetic: boolean;
  /** The message's own heading ("WARNING: Geomagnetic K-index of 4 expected"). */
  readonly title: string;
  readonly validFrom: string | null;
  readonly validTo: string | null;
  /** The G level the message names ("Noaa Scale: G1 - Minor", or the watch's highest level). */
  readonly g: number | null;
  /** A watch's levels by UTC day. */
  readonly watchDays: readonly { readonly date: string; readonly g: number }[];
  /** The message, plain text. */
  readonly text: string;
}

/** Whether a SWPC product code is about the geomagnetic field. */
export function isMagneticCode(productId: string): boolean {
  return (
    /^K0[4-9][AW]$/.test(productId) ||
    /^A[2-9]\dF$/.test(productId) ||
    productId === 'SGIW' ||
    productId === 'MSIS'
  );
}

/** What a geomagnetic product code means, in words. */
export function noaaCodeMeaning(productId: string): string {
  const k = /^K0([4-9])([AW])$/.exec(productId);
  if (k) return k[2] === 'W' ? `K-index of ${k[1]} expected` : `K-index of ${k[1]} reached`;
  const a = /^A([2-9])\dF$/.exec(productId);
  if (a) return `G${Number(a[1]) - 1} storm watch`;
  if (productId === 'SGIW') return 'Sudden impulse expected';
  if (productId === 'MSIS') return 'Sudden impulse observed';
  return 'Not about magnetics';
}

/** `/products/alerts.json` (SWPC's last 30 days of watches, warnings, alerts and summaries), newest first. */
export function parseNoaaAlerts(body: string): readonly NoaaMessage[] {
  const out: NoaaMessage[] = [];
  for (const r of rows(body)) {
    const productId = str(r.product_id) ?? '';
    const issued = noaaTime(r.issue_datetime);
    const text = typeof r.message === 'string' ? r.message.replace(/\r\n/g, '\n') : '';
    if (!productId || !issued || !text) continue;
    const line = (re: RegExp): string | null => {
      const m = re.exec(text);
      return m ? m[1]!.trim() : null;
    };
    const titleLine =
      text
        .split('\n')
        .map((l) => l.trim())
        .find((l) =>
          /^(WATCH|WARNING|ALERT|SUMMARY|CANCEL|EXTENDED WARNING|CONTINUED ALERT|CANCEL WATCH)[^:]*:/.test(l),
        ) ?? productId;
    const kind: NoaaMessageKind = /^CANCEL/.test(titleLine)
      ? 'cancel'
      : /^WATCH/.test(titleLine)
        ? 'watch'
        : /WARNING/.test(titleLine)
          ? 'warning'
          : /ALERT/.test(titleLine)
            ? 'alert'
            : /^SUMMARY/.test(titleLine)
              ? 'summary'
              : 'other';
    const at = (s: string | null): string | null => (s ? noaaMessageTime(s) : null);
    const year = Number(issued.slice(0, 4));
    const watchDays: { date: string; g: number }[] = [];
    // a watch: "Highest Storm Level Predicted by Day:" and one line of days; a cancel: "Cancelled Level Predicted:"
    // and a line per day (every cancelled day counts as none)
    const block =
      /Highest Storm Level Predicted by Day:\s*\n([^\n]*)/.exec(text)?.[1] ??
      /Cancelled Level Predicted:\s*\n((?:[A-Z][a-z]{2}\s+\d{1,2}\s*:[^\n]*\n?)+)/.exec(text)?.[1] ??
      '';
    for (const m of block.matchAll(/([A-Z][a-z]{2})\s+(\d{1,2})\s*:\s+(G(\d)|None)/g)) {
      const mo = MONTHS.indexOf(m[1]!);
      if (mo < 0) continue;
      // a watch issued in late December may name 1 January
      const y = mo < Number(issued.slice(5, 7)) - 1 - 6 ? year + 1 : year;
      watchDays.push({
        date: new Date(Date.UTC(y, mo, Number(m[2]))).toISOString().slice(0, 10),
        g: m[4] && kind !== 'cancel' ? Number(m[4]) : 0,
      });
    }
    const scale = line(/Noaa Scale:\s*G(\d)/i);
    const g =
      scale !== null ? Number(scale) : watchDays.length ? Math.max(...watchDays.map((d) => d.g)) : null;
    out.push({
      productId,
      issued,
      serial: line(/Serial Number:\s*(\d+)/),
      kind,
      magnetic: isMagneticCode(productId),
      title: titleLine.replace(/\s+/g, ' '),
      validFrom: at(
        line(/Valid From:\s*([^\n]+)/) ??
          line(/Threshold Reached:\s*([^\n]+)/) ??
          line(/Observed:\s*([^\n]+)/),
      ),
      validTo: at(line(/(?:Now Valid Until|Valid To):\s*([^\n]+)/)),
      g,
      watchDays,
      text: text.trim(),
    });
  }
  return out.sort((a, b) => (a.issued < b.issued ? 1 : a.issued > b.issued ? -1 : 0));
}

/** The geomagnetic messages issued in the last `hours` (24 by default), newest first, and how many others there were. */
export function recentMagneticMessages(
  messages: readonly NoaaMessage[],
  nowMs: number,
  hours = 24,
): { readonly magnetic: readonly NoaaMessage[]; readonly others: readonly NoaaMessage[] } {
  const recent = messages.filter(
    (m) => nowMs - Date.parse(m.issued) <= hours * 3_600_000 && Date.parse(m.issued) <= nowMs,
  );
  return { magnetic: recent.filter((m) => m.magnetic), others: recent.filter((m) => !m.magnetic) };
}

/**
 * The highest G a current NOAA watch gives `date` (UTC day), from the newest watch or cancel that names that day
 * ("THIS SUPERSEDES ANY/ALL PRIOR WATCHES"); 0 when the newest says none; null when no watch names it.
 */
export function watchLevelFor(messages: readonly NoaaMessage[], date: string): number | null {
  for (const m of messages) {
    if (!m.magnetic || (m.kind !== 'watch' && m.kind !== 'cancel')) continue;
    const d = m.watchDays.find((x) => x.date === date);
    if (d) return d.g;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------- NOAA text forecasts

export interface KpForecastText {
  readonly product: '3-Day Forecast' | 'Geomagnetic Forecast';
  readonly issued: string;
  /** 3-hour bins, ISO start times, Kp. */
  readonly kp: readonly { readonly t: string; readonly kp: number }[];
  /** Geomagnetic forecast only: probabilities (%) by day for active, minor, moderate and strong-extreme storm. */
  readonly probabilities: readonly {
    readonly date: string;
    readonly active: number;
    readonly minor: number;
    readonly moderate: number;
    readonly strong: number;
  }[];
  /** 3-day forecast only: section A's rationale, plain text. */
  readonly rationale: string | null;
}

/** SWPC's `3-day-forecast.txt` or `3-day-geomag-forecast.txt`: the Kp table (and probabilities), with the issue time. */
export function parseKpForecastText(text: string): KpForecastText | null {
  const t = text.replace(/\r\n/g, '\n');
  const product = /^:Product:\s*(3-Day Forecast|Geomagnetic Forecast)/m.exec(t)?.[1] as
    KpForecastText['product'] | undefined;
  const issued = noaaMessageTime(/^:Issued:\s*([^\n]+)/m.exec(t)?.[1] ?? '');
  if (!product || !issued) return null;
  const year = Number(issued.slice(0, 4));
  const head = /^\s+([A-Z][a-z]{2} \d{1,2})\s+([A-Z][a-z]{2} \d{1,2})\s+([A-Z][a-z]{2} \d{1,2})\s*$/m.exec(t);
  const dates = head
    ? [head[1]!, head[2]!, head[3]!].map((d) => {
        const [mon, dd] = d.split(' ') as [string, string];
        const mo = MONTHS.indexOf(mon);
        const y = mo === 0 && Number(issued.slice(5, 7)) === 12 ? year + 1 : year;
        return Date.UTC(y, mo, Number(dd));
      })
    : [];
  const kp: { t: string; kp: number }[] = [];
  if (dates.length === 3) {
    for (const m of t.matchAll(/^(\d{2})-(\d{2})UT\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/gm)) {
      const h = Number(m[1]);
      [m[3], m[4], m[5]].forEach((v, i) =>
        kp.push({ t: new Date(dates[i]! + h * 3_600_000).toISOString(), kp: Number(v) }),
      );
    }
    kp.sort((a, b) => (a.t < b.t ? -1 : 1));
  }
  const probabilities: KpForecastText['probabilities'][number][] = [];
  const prob = (label: string): number[] | null => {
    const m = new RegExp(`^${label}\\s+(\\d+)/(\\d+)/(\\d+)`, 'm').exec(t);
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
  };
  const a = prob('Active');
  const mi = prob('Minor storm');
  const mo = prob('Moderate storm');
  const st = prob('Strong-Extreme storm');
  if (a && mi && mo && st && dates.length === 3)
    for (let i = 0; i < 3; i++)
      probabilities.push({
        date: new Date(dates[i]!).toISOString().slice(0, 10),
        active: a[i]!,
        minor: mi[i]!,
        moderate: mo[i]!,
        strong: st[i]!,
      });
  const rat = /A\. NOAA Geomagnetic Activity[\s\S]*?Rationale:\s*([\s\S]*?)\n\s*\n/.exec(t);
  return {
    product,
    issued,
    kp,
    probabilities,
    rationale: rat ? rat[1]!.replace(/\s+/g, ' ').trim() : null,
  };
}

/** Of two NOAA Kp forecasts, the newer issue (spec §3.2: they can disagree; the newest wins and says which). */
export function newestForecast(...forecasts: readonly (KpForecastText | null)[]): KpForecastText | null {
  let best: KpForecastText | null = null;
  for (const f of forecasts) if (f && f.kp.length && (!best || f.issued > best.issued)) best = f;
  return best;
}

/** The forecast discussion's issue time and its first geospace forecast sentence (the reading list's NOAA line). */
export function discussionItem(text: string): ReadingItem | null {
  const t = text.replace(/\r\n/g, '\n');
  const issued = noaaMessageTime(/^:Issued:\s*([^\n]+)/m.exec(t)?.[1] ?? '');
  if (!issued) return null;
  const geo = /Geospace[\s\S]*?\.Forecast\.\.\.\s*\n([\s\S]*?)(\n\s*\n|$)/.exec(t);
  const sentence =
    (geo?.[1] ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .split(/(?<=\.)\s/)[0] ?? '';
  return {
    source: 'noaa_swpc',
    title: sentence ? `Forecast discussion: ${sentence}` : 'Forecast discussion',
    date: issued,
    url: 'https://www.spaceweather.gov/products/forecast-discussion',
  };
}

// ---------------------------------------------------------------------------------------------- Enlil and DONKI

export interface EnlilFrame {
  readonly path: string;
  /** From the file name (`…_20261020T060000.jpg`), ISO UTC; null when it has none. */
  readonly time: string | null;
}

/** `/products/animations/enlil.json`: the frame paths (only SWPC's Enlil folder), oldest first. */
export function parseEnlilFrames(body: string): readonly EnlilFrame[] {
  return parseEnlilList(body);
}

/** The frame closest to now among those not after it (the Enlil model's "now"), or the last frame. */
export function latestEnlilFrame(frames: readonly EnlilFrame[], nowMs: number): EnlilFrame | null {
  let best: EnlilFrame | null = null;
  for (const f of frames) if (f.time && Date.parse(f.time) <= nowMs) best = f;
  return best ?? frames[frames.length - 1] ?? null;
}

export interface EarthArrival {
  readonly simulationId: string;
  readonly completed: string | null;
  /** Estimated shock arrival at Earth, ISO UTC. */
  readonly arrival: string;
  readonly glancing: boolean;
  readonly minorImpact: boolean;
  /** Kp estimate for a 90° clock angle (the middle of DONKI's four). */
  readonly kp90: number | null;
  readonly kpMax: number | null;
  readonly cmeSpeed: number | null;
  readonly cmeStart: string | null;
  readonly link: string | null;
}

const donkiTime = (s: unknown): string | null =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z$/.test(s) ? new Date(s).toISOString() : null;

/** DONKI `WSAEnlilSimulations`: the runs with an Earth arrival, newest run first (a model estimate, not a forecast). */
export function parseDonkiEarthArrivals(body: string): readonly EarthArrival[] {
  const out: EarthArrival[] = [];
  for (const r of rows(body)) {
    const arrival = donkiTime(r.estimatedShockArrivalTime);
    if (!arrival) continue;
    const cme =
      Array.isArray(r.cmeInputs) && r.cmeInputs[0] && typeof r.cmeInputs[0] === 'object'
        ? (r.cmeInputs[0] as Record<string, unknown>)
        : {};
    const kps = [r.kp_18, r.kp_90, r.kp_135, r.kp_180].map(num).filter((x): x is number => x !== null);
    out.push({
      simulationId: str(r.simulationID) ?? '',
      completed: donkiTime(r.modelCompletionTime),
      arrival,
      glancing: r.isEarthGB === true,
      minorImpact: r.isEarthMinorImpact === true,
      kp90: num(r.kp_90),
      kpMax: kps.length ? Math.max(...kps) : null,
      cmeSpeed: num(cme.speed),
      cmeStart: donkiTime(cme.cmeStartTime),
      link: typeof r.link === 'string' ? sourceLink('nasa_donki', r.link) : null,
    });
  }
  return out.sort((a, b) => ((a.completed ?? '') < (b.completed ?? '') ? 1 : -1));
}

/** The highest observed Kp in the 3-hour bins from `fromIso` for `hours` (what happened after a forecast arrival). */
export function observedKpMax(kp: readonly KpPoint[], fromIso: string, hours: number): number | null {
  const a = Date.parse(fromIso);
  const vals = kp
    .filter((p) => p.kind !== 'predicted')
    .filter((p) => {
      const t = Date.parse(p.t);
      return t + 3 * 3_600_000 > a && t < a + hours * 3_600_000;
    })
    .map((p) => p.kp);
  return vals.length ? Math.max(...vals) : null;
}

/** DONKI `notifications`: the weekly report and CME messages as reading items (title from "## Message Type: …"). */
export function parseDonkiNotifications(body: string): readonly ReadingItem[] {
  const out: ReadingItem[] = [];
  for (const r of rows(body)) {
    const type = str(r.messageType) ?? '';
    if (type !== 'Report' && type !== 'CME') continue;
    const date = donkiTime(r.messageIssueTime);
    const url = typeof r.messageURL === 'string' ? sourceLink('nasa_donki', r.messageURL) : null;
    const head = /## Message Type:\s*([^\n]+)/
      .exec(typeof r.messageBody === 'string' ? r.messageBody : '')?.[1]
      ?.trim();
    if (!date || !url) continue;
    out.push({
      source: 'nasa_donki',
      title: head || (type === 'CME' ? 'CME message' : 'Weekly report'),
      date,
      url,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- web pictures and text

export interface BomPictures {
  readonly pc3Icon: string | null;
  readonly geostatIcon: string | null;
  readonly geostat: BomGeostat | null;
}

const GIF_DATA = /^data:image\/gif;base64,[A-Za-z0-9+/=]+$/;

/** The proxy's `bom_pictures`: the two GIFs as `data:` addresses (only GIF data addresses are kept) and the GEOSTAT line. */
export function parseBomPictures(body: string): BomPictures | null {
  const v = lenientJson(body);
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const pic = (x: unknown): string | null => (typeof x === 'string' && GIF_DATA.test(x) ? x : null);
  const g = o.geostat && typeof o.geostat === 'object' ? (o.geostat as Record<string, unknown>) : null;
  return {
    pc3Icon: pic(o.pc3_icon),
    geostatIcon: pic(o.geostat_icon),
    geostat: g && typeof g.text === 'string' ? { text: g.text, last_updated: str(g.last_updated) } : null,
  };
}

/** The proxy's `spaceweather_com`. */
export function parseSpaceweatherCom(body: string): SpaceweatherComNow | null {
  const v = lenientJson(body);
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const date = str(o.date);
  const stories = (Array.isArray(o.stories) ? o.stories : [])
    .map((s): SpaceweatherComStory | null => {
      if (!s || typeof s !== 'object') return null;
      const r = s as Record<string, unknown>;
      const title = str(r.title);
      return title ? { title, summary: str(r.summary) ?? '' } : null;
    })
    .filter((s): s is SpaceweatherComStory => s !== null)
    .slice(0, SWCOM_STORIES_MAX);
  return {
    speed_km_s: num(o.speed_km_s),
    density_cm3: num(o.density_cm3),
    updated_text: str(o.updated_text),
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null,
    stories,
  };
}

/**
 * spaceweather.com's daily stories as reading items (R151, decision 689): the headline, a sentence or two, the page's
 * date and a link to its home page, where the day's stories are (the site has no address of its own per story). An
 * answer without its page date gives none: an undated story could never age out of the list.
 */
export function spaceweatherComItems(now: SpaceweatherComNow | null): readonly ReadingItem[] {
  const date = now?.date;
  if (!date) return [];
  const url = sourceLink('spaceweather_com', spaceWeatherSource('spaceweather_com').home);
  if (!url) return [];
  return (now.stories ?? []).map((s) => ({
    source: 'spaceweather_com' as const,
    title: s.title,
    date,
    url,
    ...(s.summary ? { summary: s.summary } : {}),
  }));
}

/** The note under the reading list beside spaceweather.com's stories (decision 689: credit and where its numbers come from). */
export const SPACEWEATHER_COM_NOTE =
  'Spaceweather.com’s stories are by Dr Tony Phillips, shown with permission. Its numbers come from NOAA.';

/** The note the reading list carries: spaceweather.com's when it lists one of its stories, else null. */
export function readingCredit(items: readonly ReadingItem[]): string | null {
  return items.some((i) => i.source === 'spaceweather_com') ? SPACEWEATHER_COM_NOTE : null;
}

/** At most this many of spaceweather.com's stories in the reading list, so the other sources keep their place. */
export const READING_SWCOM_MAX = 3;

/** The proxy's `bom_site_news`, `swpc_news`, `nasa_blog`: items whose link stays on the source's own hosts. */
export function parseReadingItems(body: string): readonly ReadingItem[] {
  return rows(body)
    .map((r) => {
      const source = str(r.source) as ReadingItem['source'] | null;
      const url = source && typeof r.url === 'string' ? sourceLink(source, r.url) : null;
      const title = str(r.title);
      const date = str(r.date);
      const summary = str(r.summary);
      return source && url && title && date
        ? { source, title, date, url, ...(summary ? { summary: summary.slice(0, 400) } : {}) }
        : null;
    })
    .filter((x): x is ReadingItem => x !== null);
}

/**
 * The reading list (spec §4.3 H, decisions 693, 689): up to 8, newest first, from NOAA's discussion, spaceweather.com's
 * stories of the last 2 days (at most `READING_SWCOM_MAX`; R151), SWPC news of the last 14 days, BoM Site News of the
 * last 60 days, DONKI reports and CME messages of the last 7 days and NASA Solar Cycle 25 posts of the last 30 days.
 * `pinned` (the BoM warning's comments, when current) comes first. A date without a time (BoM, spaceweather.com)
 * sorts after the same day's timed items.
 */
export function readingList(
  groups: {
    readonly discussion?: ReadingItem | null;
    readonly swcom?: readonly ReadingItem[];
    readonly swpc?: readonly ReadingItem[];
    readonly bom?: readonly ReadingItem[];
    readonly donki?: readonly ReadingItem[];
    readonly nasa?: readonly ReadingItem[];
  },
  nowMs: number,
  max = 8,
): readonly ReadingItem[] {
  const within = (days: number) => (i: ReadingItem) => {
    const t = Date.parse(i.date);
    return Number.isFinite(t) && nowMs - t <= days * 86_400_000 && t <= nowMs + 86_400_000;
  };
  const items = [
    ...(groups.swcom ?? []).filter(within(2)).slice(0, READING_SWCOM_MAX),
    ...(groups.swpc ?? []).filter(within(14)),
    ...(groups.bom ?? []).filter(within(60)),
    ...(groups.donki ?? []).filter(within(7)),
    ...(groups.nasa ?? []).filter(within(30)),
  ].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const head = groups.discussion ? [groups.discussion] : [];
  return [...head, ...items].slice(0, max);
}

/** The plain links of decision 693 (spaceweather.com and SpaceWeatherLive: links only). */
export const SPACE_WEATHER_LINKS: readonly { readonly label: string; readonly url: string }[] = [
  { label: 'BoM Space Weather Services', url: 'https://www.sws.bom.gov.au/' },
  { label: 'NOAA SWPC', url: 'https://www.spaceweather.gov/' },
  { label: 'NASA CCMC DONKI', url: 'https://ccmc.gsfc.nasa.gov/tools/DONKI/' },
  { label: 'Spaceweather.com', url: 'https://www.spaceweather.com/' },
  { label: 'SpaceWeatherLive', url: 'https://www.spaceweatherlive.com/' },
];

// ---------------------------------------------------------------------------------------------- Kp, Ap, daylight

/** The standard Kp to ap table (thirds: 0, 0+, 1-, 1, …, 9). */
const AP_OF_KP = [
  0, 2, 3, 4, 5, 6, 7, 9, 12, 15, 18, 22, 27, 32, 39, 48, 56, 67, 80, 94, 111, 132, 154, 179, 207, 236, 300,
  400,
];

/** ap of a Kp value (rounded to the nearest third). */
export function kpToAp(kp: number): number {
  const i = Math.max(0, Math.min(AP_OF_KP.length - 1, Math.round(kp * 3)));
  return AP_OF_KP[i]!;
}

/** Today's Ap so far (mean ap of the UTC day's 3-hour bins held, observed or estimated); null without a bin. */
export function apSoFar(kp: readonly KpPoint[], nowMs: number): number | null {
  const day = new Date(nowMs).toISOString().slice(0, 10);
  const bins = kp.filter((p) => p.kind !== 'predicted' && p.t.startsWith(day) && Date.parse(p.t) <= nowMs);
  return bins.length ? Math.round(bins.reduce((s, p) => s + kpToAp(p.kp), 0) / bins.length) : null;
}

/**
 * Sunrise to sunset (UTC ms) at a site on a UTC date, from the sun's declination and the equation of time
 * (Spencer 1971, as the MLT windows use); null for polar day or night.
 */
export function daylightUtc(lat: number, lon: number, dateIso: string): readonly [number, number] | null {
  const [y, m, d] = dateIso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const day0 = Date.UTC(y, m - 1, d);
  const doy = Math.round((day0 - Date.UTC(y, 0, 0)) / 86_400_000);
  const g = (2 * Math.PI * (doy - 1)) / 365;
  const decl =
    0.006918 -
    0.399912 * Math.cos(g) +
    0.070257 * Math.sin(g) -
    0.006758 * Math.cos(2 * g) +
    0.000907 * Math.sin(2 * g) -
    0.002697 * Math.cos(3 * g) +
    0.00148 * Math.sin(3 * g);
  const eqtMin =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(g) -
      0.032077 * Math.sin(g) -
      0.014615 * Math.cos(2 * g) -
      0.040849 * Math.sin(2 * g));
  const phi = (lat * Math.PI) / 180;
  const cosH =
    (Math.cos((90.833 * Math.PI) / 180) - Math.sin(phi) * Math.sin(decl)) / (Math.cos(phi) * Math.cos(decl));
  if (cosH < -1 || cosH > 1) return null;
  const hDeg = (Math.acos(cosH) * 180) / Math.PI;
  const noonMin = 720 - 4 * lon - eqtMin;
  return [day0 + (noonMin - 4 * hDeg) * 60_000, day0 + (noonMin + 4 * hDeg) * 60_000];
}

/**
 * The highest predicted or estimated Kp over the rest of today's daylight at the site (or tomorrow's daylight once
 * the sun has set), with that window; null without a forecast bin in it.
 */
export function kpForDaylight(
  kp: readonly { readonly t: string; readonly kp: number }[],
  site: { lat: number; lon: number },
  nowMs: number,
): { readonly kp: number; readonly from: number; readonly to: number; readonly tomorrow: boolean } | null {
  // the site's local date (by longitude) decides "today"
  const localDay = new Date(nowMs + (site.lon / 15) * 3_600_000).toISOString().slice(0, 10);
  let win = daylightUtc(site.lat, site.lon, localDay);
  let tomorrow = false;
  if (win && nowMs > win[1]) {
    const next = new Date(Date.parse(`${localDay}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
    win = daylightUtc(site.lat, site.lon, next);
    tomorrow = true;
  }
  if (!win) return null;
  const from = Math.max(nowMs, win[0]);
  const to = win[1];
  const vals = kp
    .filter((p) => {
      const t = Date.parse(p.t);
      return t + 3 * 3_600_000 > from && t < to;
    })
    .map((p) => p.kp);
  return vals.length ? { kp: Math.max(...vals), from, to, tomorrow } : null;
}

// ---------------------------------------------------------------------------------------------- ages

/** A section's numbers turn the warn colour with "Old: …" after this (spec §4.5). */
export const SPACE_WEATHER_OLD_MS = 30 * 60_000;
/** After this the verdict says "Unknown" (spec §4.5). */
export const SPACE_WEATHER_UNKNOWN_MS = 3 * 3_600_000;

/** "3 min ago", "2 h 10 min ago", "just now". */
export function ageWords(fetchedAtIso: string, nowMs: number): string {
  const min = Math.max(0, Math.round((nowMs - Date.parse(fetchedAtIso)) / 60_000));
  if (!Number.isFinite(min)) return 'unknown';
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h >= 48) return `${Math.round(h / 24)} days ago`;
  return m ? `${h} h ${m} min ago` : `${h} h ago`;
}

export type AgeState = 'fresh' | 'old' | 'unknown';

export function ageState(fetchedAtIso: string | null, nowMs: number): AgeState {
  if (!fetchedAtIso) return 'unknown';
  const age = nowMs - Date.parse(fetchedAtIso);
  if (!Number.isFinite(age)) return 'unknown';
  return age > SPACE_WEATHER_UNKNOWN_MS ? 'unknown' : age > SPACE_WEATHER_OLD_MS ? 'old' : 'fresh';
}

/** "05:45 UTC (13:45 AWST)" style: the UTC clock and the project's local clock beside it. */
export function utcAndLocal(iso: string, offsetH: number, zoneLabel: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const hm = (ms: number): string => new Date(ms).toISOString().slice(11, 16);
  return `${hm(t)} UTC (${hm(t + offsetH * 3_600_000)} ${zoneLabel})`;
}

/** "26 Oct 05:45 UTC". */
export function dayAndUtc(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.toISOString().slice(11, 16)} UTC`;
}

/** NOAA's G level in words. */
export function gScaleWords(g: number | null): string {
  if (g === null) return 'unknown';
  return ['G0 (no storm)', 'G1 (minor)', 'G2 (moderate)', 'G3 (strong)', 'G4 (severe)', 'G5 (extreme)'][
    Math.max(0, Math.min(5, Math.round(g)))
  ]!;
}

/** The words BoM uses for a K value (Australian region): quiet, unsettled, active, minor/major/severe storm. */
export function kWords(k: number | null): string {
  if (k === null) return 'unknown';
  if (k <= 2) return 'quiet';
  if (k === 3) return 'unsettled';
  if (k === 4) return 'active';
  if (k === 5) return 'minor storm';
  if (k === 6) return 'major storm';
  return 'severe storm';
}
